import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { generalAnimations } from 'app/shared/animations/animations';
import { AuthenticationState } from 'app/shared/authentication/models/login-state.enum';
import { Clinic } from 'app/shared/clinics/models/clinic.model';
import { LanguageCulureNameEnum } from 'app/shared/layout/models/languageCulureNameEnum';
import { Identifier, LocalStorageService } from 'app/shared/storage/services/local-storage.service';
import { Subscription } from 'rxjs';

import { AuthService } from '../../services/auth.service';
import { NavigationHandlerService } from '../../services/navigation-handler.service';
import { TranslateService } from '@ngx-translate/core';
import { LoginType } from '../../models/login-type.model';

enum Mode {
  Email = 1,
  SMS = 2,
  Code = 3,
}

@Component({
  selector: 'core-auth-one-time-code-login',
  templateUrl: './one-time-code-login.component.html',
  styleUrls: ['./one-time-code-login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: generalAnimations,
  standalone: false,
})
export class OneTimeCodeLoginComponent implements OnInit, OnDestroy {
  @Input() canReturn = false;
  @Input() previousCanReturn = false;
  @Input() previousState!: AuthenticationState;
  @Input() state: AuthenticationState = AuthenticationState.LoginSelection;
  @Input() loginType!: LoginType;
  @Output() loginEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() loginSelectionEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() closeModalEmitter: EventEmitter<any> = new EventEmitter<any>();
  userInfoFormGroup?: UntypedFormGroup;
  oneTimeAccessFormGroup = new UntypedFormGroup({
    accessCode: new UntypedFormControl(null, Validators.required),
  });
  isSubmitted = false;
  loginInvalid = false;
  userName?: string;
  clinic: Clinic = JSON.parse(localStorage.getItem('clinic') || '{}');
  canSendSms = false;
  selectedMode?: Mode;
  Mode = Mode;
  isBooking = false;

  private subscription: Subscription = new Subscription();
  constructor(
    private ref: ChangeDetectorRef,
    private authService: AuthService,
    private localStorageService: LocalStorageService,
    private navigationHandlerService: NavigationHandlerService,
    public translateService: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.state === AuthenticationState.OneTimeLoginEmail) {
      this.selectedMode = Mode.Email;
      this.onSelectEmail();
    } else if (this.state === AuthenticationState.OneTimeLoginSms) {
      this.selectedMode = Mode.SMS;
      this.onSelectSms();
    } else if (this.state === AuthenticationState.ReceptionCode) {
      this.selectedMode = Mode.Code;
    }
  }

  displayRequiredError(control: AbstractControl): boolean {
    return control.hasError('required') && (control.touched || this.isSubmitted);
  }

  onSelectEmail(): void {
    this.userInfoFormGroup = new UntypedFormGroup(
      {
        userIdentifier: new UntypedFormControl(this.loginType.value, [
          Validators.required,
          Validators.email,
        ]),
      },
      {}
    );
    this.userInfoFormGroup.get('userIdentifier')?.markAsDirty();
    this.onSendCode();
  }

  displayPatternError(control: AbstractControl): boolean {
    return control?.hasError('validateNoPlus');
  }

  onSelectSms(): void {
    this.userInfoFormGroup = new UntypedFormGroup({
      userIdentifier: new UntypedFormControl(this.loginType.value, [
        Validators.required,
        Validators.pattern(/^[0-9]{10}$/),
      ]),
    });
    this.userInfoFormGroup.get('userIdentifier')?.markAsDirty();
    this.onSendCode();
  }

  onSendCode(): void {
    if (this.userInfoFormGroup?.valid) {
      this.subscription.add(
        this.authService.getLoginAccessCode(this.userInfoFormGroup).subscribe(() => {
          this.ref.markForCheck();
        })
      );
    }
  }

  onOneTimeCodeSubmit(): void {
    this.isSubmitted = true;
    if (this.oneTimeAccessFormGroup?.valid) {
      this.subscription.add(
        this.authService
          .useOneTimeCode(this.oneTimeAccessFormGroup.get('accessCode')?.value)
          .subscribe(
            res => {
              this.loginInvalid = false;
              const cmUser = this.authService.processToken(res.token);
              if (cmUser.correspondenceLanguageId) {
                this.localStorageService.setStorageItem(
                  Identifier.TYPE_LANGUAGE,
                  LanguageCulureNameEnum[cmUser.correspondenceLanguageId]
                );
              }

              this.navigationHandlerService.handleNavigation();
              this.closeModalEmitter.emit();
            },
            () => {
              this.loginInvalid = true;
              this.ref.markForCheck();
            }
          )
      );
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onBack(): void {
    if (this.previousState === AuthenticationState.LogIn) {
      this.loginEmitter.emit();
    } else if (this.previousState === AuthenticationState.LoginSelection) {
      this.loginSelectionEmitter.emit();
    }
  }
}
